import { Link } from 'react-router-dom';

export function MagicPage() {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <Link
          to="/ignus"
          className="text-text-muted hover:text-accent-gold transition-colors inline-flex items-center gap-1"
        >
          <BackIcon className="w-4 h-4" />
          Back to Ignus
        </Link>
      </div>

      <h1 className="font-display text-3xl font-bold text-text-primary mb-6">
        Magic & the Titans
      </h1>

      <div className="space-y-6">
        {/* Introduction */}
        <div className="card p-6 border-orange-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <SparkleIcon className="w-5 h-5 text-orange-500" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              Magic in Ignus
            </h2>
          </div>
          <p className="text-text-secondary">
            Magic is not a neutral force in Ignus. The source of magical power matters deeply to the
            people of this world—it determines whether you are celebrated as a protector or hunted
            as a dangerous aberration.
          </p>
        </div>

        {/* The Three Sources */}
        <div id="three-sources" className="card p-6">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            The Three Sources of Magic
          </h2>

          <div className="space-y-4">
            {/* Titanic Magic */}
            <div id="titanic" className="bg-background-elevated rounded-lg p-4 border-l-4 border-accent-gold">
              <div className="flex items-center gap-2 mb-2">
                <CrownIcon className="w-5 h-5 text-accent-gold" />
                <h3 className="font-semibold text-accent-gold">Titanic (Divine) Magic</h3>
              </div>
              <p className="text-text-secondary mb-2">
                Power granted by the Titans of the Crystal Assembly to their faithful followers.
                Clerics and Paladins channel this divine energy to heal, protect, and smite.
              </p>
              <p className="text-sm text-green-400">
                Considered wholesome, good, and healthy by society.
              </p>
            </div>

            {/* Nature Magic */}
            <div id="nature" className="bg-background-elevated rounded-lg p-4 border-l-4 border-green-500">
              <div className="flex items-center gap-2 mb-2">
                <LeafIcon className="w-5 h-5 text-green-400" />
                <h3 className="font-semibold text-green-400">Nature Magic</h3>
              </div>
              <p className="text-text-secondary mb-2">
                The primordial power of Ignus itself, channeled by druids and rangers. This magic
                flows from the world's natural energies—what The Circle believes to be the dreams of
                a sleeping super-titan.
              </p>
              <p className="text-sm text-green-400">
                Considered wholesome, good, and healthy by society.
              </p>
            </div>

            {/* Arcane Magic */}
            <div id="arcane" className="bg-background-elevated rounded-lg p-4 border-l-4 border-red-500">
              <div className="flex items-center gap-2 mb-2">
                <SkullIcon className="w-5 h-5 text-red-400" />
                <h3 className="font-semibold text-red-400">Arcane Magic</h3>
              </div>
              <p className="text-text-secondary mb-2">
                The forbidden art. Magic drawn from raw arcane forces, manipulated through study
                (wizards), innate talent (sorcerers), or dark pacts (warlocks). This is the power
                that nearly destroyed the world in The Destruction.
              </p>
              <p className="text-sm text-red-400">
                Considered disgusting, deviant, and dangerous by most of society.
              </p>
            </div>
          </div>
        </div>

        {/* The Crystal Assembly */}
        <div id="crystal-assembly" className="card p-6 border-accent-gold/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-accent-gold/10 flex items-center justify-center">
              <CrystalIcon className="w-5 h-5 text-accent-gold" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              The Crystal Assembly
            </h2>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              The Titans of the Crystal Assembly are the divine beings who took pity on mortals
              during The Destruction. By lending their magical energies to their followers and
              sometimes even directly intervening at great risk to their own existence, they helped
              smash the enemies of mortalkind and began the process of healing those broken by the
              conflict.
            </p>
            <p>
              Today, The Crystal Assembly is worshipped through The Seat, their faithful servants
              organized into churches, temples, and other communities of devotion across Ignus.
            </p>

            <div className="bg-background-card rounded-lg p-4">
              <h4 className="font-medium text-text-primary mb-2">The Seat's Hierarchy</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold">•</span>
                  <span>
                    <strong className="text-accent-gold">The High Seat</strong> — Supreme leader of
                    the faithful
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold">•</span>
                  <span>
                    <strong className="text-accent-gold">Council of Primarchs</strong> —
                    Representatives from each major faith
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-gold">•</span>
                  <span>
                    <strong className="text-accent-gold">The Silver Fist of Heaven</strong> — Leader
                    of the paladins
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* The Shadow Assembly */}
        <div className="card p-6 border-purple-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <MoonIcon className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              The Shadow Assembly
            </h2>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              Not all Titans agreed with The Crystal Assembly's decision to intervene in mortal
              affairs. Despite the protests of some of their peers—now known as{' '}
              <span className="text-purple-400">the Shadow Assembly</span>, or simply "the
              Shadowed"—the Crystal Assembly chose to act.
            </p>
            <p>
              The Shadowed Titans remain mysterious figures. Whether they opposed intervention out of
              indifference, wisdom, or darker motives remains a matter of theological debate.
              Warlocks are sometimes rumored to draw power from these enigmatic beings.
            </p>
          </div>
        </div>

        {/* The Ignus Hypothesis */}
        <div className="card p-6 border-green-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <GlobeIcon className="w-5 h-5 text-green-400" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              The Ignus Hypothesis
            </h2>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              The Circle believes that Ignus is actually a sleeping super-titan—or maybe even a true
              deity—and that all living beings (including the titans) are a creation of the magical
              energy emitted by Ignus in its slumber.
            </p>
            <p className="italic text-text-muted">
              In their minds, we are all, in effect, products of Ignus's dreams.
            </p>
            <p>
              This belief shapes The Circle's mission: they see themselves as shepherds and
              protectors of Ignus's magical flow. Their version of the apocalypse is Ignus waking
              up. To keep it sleeping, they must preserve magical and natural balance because this
              creates the comforting conditions that will keep Ignus asleep.
            </p>

            <div className="bg-background-elevated rounded-lg p-4">
              <h4 className="font-medium text-text-primary mb-2">The Circle's Structure</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-400">•</span>
                  <span>
                    <strong className="text-green-400">The Stump</strong> — A triumvirate of the
                    three oldest druids, theoretically equal in status
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">•</span>
                  <span>
                    <strong className="text-green-400">Speaker for the Stump</strong> — The fourth
                    oldest druid, who delivers all decrees
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">•</span>
                  <span>
                    <strong className="text-green-400">The Circles</strong> — Individual druid
                    circles, each with their own focus
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* The Forbidden Arts */}
        <div className="card p-6 border-red-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <WarningIcon className="w-5 h-5 text-red-400" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              The Forbidden Arts
            </h2>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              In the aftermath of The Destruction, arcane powers were outlawed and those who
              practiced arcane magic were hunted down and... dealt with.
            </p>
            <p>
              The three great civilizations that caused The Destruction—The Raathan Archive, The
              Shaungin Empire, and the People of Kaltash—were all arcanocracies. Their weapons of
              mass destruction and arcane hubris nearly wiped out all of mortalkind.
            </p>
            <p>
              Today, anyone who practices arcane magic is considered one of The Shunned. They must
              hide their abilities or face severe consequences. Sorcerers are encouraged to join the
              clergy or druids to channel their innate powers in "healthy" ways. Wizards and
              warlocks are treated as dangerous criminals at best, existential threats at worst.
            </p>
          </div>
        </div>

        {/* Quick Links */}
        <div className="card p-6 bg-background-elevated">
          <h2 className="font-display text-lg font-semibold text-text-primary mb-4">
            Continue Exploring
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/ignus/factions"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <FactionIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">The Four Factions</span>
            </Link>
            <Link
              to="/ignus/spells"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <SpellIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Browse Spells</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
    </svg>
  );
}

function LeafIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function SkullIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H9v-1a3 3 0 016 0v1zm0 0h3m-3 0h-3m3-5a4 4 0 00-6 0" />
    </svg>
  );
}

function CrystalIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12l8-4.5M12 12v9M12 12L4 7.5" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function FactionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function SpellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}
